import flatten from 'lodash/flatten';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, TemplateRef,
  OnInit, OnDestroy, ViewChild, AfterViewInit, ElementRef, HostBinding, HostListener,
} from '@angular/core';

import { isMobileView } from '@app/lib/screen-sizes';

import 'style-loader!./hierarchy-multi-select-with-counter.component.deep.scss';

export interface ISelectOption {
  label: string;
  key: string;
  isPreSelected?: boolean;
  areChildrenSingleSelect?: boolean;
  children?: ISelectOption[];
  /** Can only be used for top-level options */
  isHeader?: boolean;
  disabled?: boolean;
}

export const DEFAULT_PLACEHOLDER: string = $localize`Select`;
export const DEFAULT_SEARCH_RESULT_CAP: number = -1;
export const DEFAULT_FORM_VARIANT: boolean = false;
export const DEFAULT_ALWAYS_SHOW_CONDENSED_MESSAGE: boolean = false;
export const DEFAULT_FOOTER_THRESHOLD: number = 2;
export const DEFAULT_FULL_WIDTH: boolean = false;
export const DEFAULT_DROPDOWN_MAX_WIDTH: number = 360;
export const DEFAULT_EMIT_ONLY_INDEPENDENT_SELECTIONS: boolean = true;
export const DEFAULT_IS_DISABLED: boolean = false;
export const DEFAULT_DISABLED_TOOLTIP_MESSAGE: string = undefined;
export const DEFAULT_PREFIX_ICON_ONLY: boolean = false;
export const DEFAULT_SHOW_SUFFIX_ICON: boolean = true;
export const DEFAULT_CAN_SEARCH_OPTIONS: boolean = false;
export const DEFAULT_DROPDOWN_LIST_MAX_HEIGHT: string = '260px';

@Component({
  selector: 'hierarchy-multi-select-with-counter',
  templateUrl: './hierarchy-multi-select-with-counter.component.html',
  styleUrls: ['./hierarchy-multi-select-with-counter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HierarchyMultiSelectWithCounterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('dropdownTriggerRef', { static: false }) public dropdownTrigger: ElementRef<HTMLButtonElement>;
  @HostBinding('class.w-100') @Input() public fullWidth: boolean = DEFAULT_FULL_WIDTH;
  @HostListener('window:resize')
  onResize(): void {
    this.isMobileView = isMobileView();
  }

  @Input() public isLoading: boolean = false;
  @Input() public set options(options: ISelectOption[]) {
    this.options$.next(options);
  }

  /** To set personalized dropdown list max height (default: 260px as per current css). */
  @Input() public overrideDropdownListMaxHeight: string = DEFAULT_DROPDOWN_LIST_MAX_HEIGHT;

  @Input() public isDisabled: boolean = DEFAULT_IS_DISABLED;
  @Input() public disabledTooltipMessage: string = DEFAULT_DISABLED_TOOLTIP_MESSAGE;
  /** If a higher-level option is selected, whether to filter out its sub-options in the output (default: true). */
  @Input() public emitOnlyIndependentSelections: boolean = DEFAULT_EMIT_ONLY_INDEPENDENT_SELECTIONS;
  @Input() public canSearchOptions: boolean = DEFAULT_CAN_SEARCH_OPTIONS;
  @Input() public placeholder: string = DEFAULT_PLACEHOLDER;
  @Input() public condensedMessage: string = null;
  /** Whether to show the condensed message even if only one option is selected (default: false). */
  @Input() public alwaysShowCondensedMessage: boolean = DEFAULT_ALWAYS_SHOW_CONDENSED_MESSAGE;
  @Input() public prefixIconTemplate: TemplateRef<any> = null;
  @Input() public noResultsMessageTemplate: TemplateRef<any> = null;
  @Input() public prefixIconOnly: boolean = DEFAULT_PREFIX_ICON_ONLY;
  @Input() public showSuffixIcon: boolean = DEFAULT_SHOW_SUFFIX_ICON;
  @Input() public formVariant: boolean = DEFAULT_FORM_VARIANT;
  /** The minimum number of options which must be selected to show the footer banner (default: 2). */
  @Input() public footerThreshold: number = DEFAULT_FOOTER_THRESHOLD;
  @Input() public set searchResultCap(searchResultCap: number) {
    this.searchResultCap$.next(searchResultCap);
  }
  /** To Enable the Radio Options for Single Option Selection at Root Level */
  @Input() public isRadioSelectionEnabled: boolean = false;
  @Input() public closeDropdownOnClear: boolean = true;
  @Input() public hideRadioIcon: boolean = false;
  @Input() public isSmall: boolean = false;
  // TODO remove this during feature flag removal
  public isExperimentalImplementation: boolean = false;
  @Input() public customOverlayClassName: string;
  @Input() public dialogOpen: boolean = false;
  @Input() public customTriggerButtonTemplate: TemplateRef<any>;
  @Input() public overlayClassName: string;
  @Output() public readonly update: EventEmitter<ISelectOption[]> = new EventEmitter();
  @Output() public readonly dialogClose: EventEmitter<void> = new EventEmitter();
  @Output() public readonly dropDownClose: EventEmitter<void> = new EventEmitter();

  public options$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject([]);
  public search$: BehaviorSubject<string> = new BehaviorSubject('');
  public searchResultCap$: BehaviorSubject<number> = new BehaviorSubject(DEFAULT_SEARCH_RESULT_CAP);
  public isDropDownMenuOpen: boolean = false;

  /** The parent option of the active options. */
  public activeRootOption$: BehaviorSubject<ISelectOption> = new BehaviorSubject(null);
  public activeOptions: ISelectOption[] = [];

  public selectedOptions$: BehaviorSubject<Set<ISelectOption>> = new BehaviorSubject(new Set());
  /** The independent selected options (options which are selected, but whose parents are not selected). */
  public independentSelectedOptions$: BehaviorSubject<Set<ISelectOption> > = new BehaviorSubject(new Set());
  /** The indeterminate options (options which are not selected, but whose sub-options are). */
  public indeterminateOptions: Set<ISelectOption> = new Set();

  /** If there is only one selected option, the selected option. Otherwise, null. */
  public onlySelectedOption: ISelectOption | null = null;
  public selectedOptionsTooltip: string = '';

  /** A mapping of the options to their ancestors (in root->option order). */
  public ancestors: Map<ISelectOption, ISelectOption[]> = new Map();

  /** Allows the dropdown to expand up to the width of the dropdown trigger if its width exceeds the default dropdown width. */
  public dropdownWidthOverride: string = undefined;

  public hasChanged: boolean = false;

  public isMobileView: boolean = isMobileView();

  public get showBadge(): boolean {
    return !this.prefixIconOnly && this.independentSelectedOptions$.value.size > (this.alwaysShowCondensedMessage ? 0 : 1);
  }

  public get buttonText(): string {
    if (this.independentSelectedOptions$.value.size === 0) return this.placeholder;

    const condensedMessage: string = this.condensedMessage || this.placeholder;
    if (this.alwaysShowCondensedMessage) return condensedMessage;
    if (this.onlySelectedOption) return this.onlySelectedOption.label;
    return condensedMessage;
  }

  public get isFooterShown(): boolean {
    return (this.independentSelectedOptions$.getValue()).size >= this.footerThreshold &&
    !(this.activeRootOption$.getValue()) && !(this.search$.getValue());
  }

  private flattenedOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject([]);

  private resizeObserver: ResizeObserver = new ResizeObserver(this.onWindowResize.bind(this));

  private destroyed$: Subject<void> = new Subject();

  constructor(
    private cdr: ChangeDetectorRef,
  ) { }

  public ngOnInit(): void {
    this.options$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((options: ISelectOption[]) => {
        this.ancestors = this.mapAncestors(options);

        const flattenedOptions: ISelectOption[] = this.flattenOptions(options);
        this.flattenedOptions$.next(flattenedOptions);

        const activeRootKey: string = this.activeRootOption$.value?.key;
        if (activeRootKey) {
          const analogousOption: ISelectOption = this.flattenedOptions$.value
            .find((option: ISelectOption) => option.key === activeRootKey) || null;
          this.activeRootOption$.next(analogousOption);
        }

        // Respect the initial state of any incoming options (mark any options with isPreSelected=true as selected).
        this.deriveSelectedOptionsFromInitialState(options, flattenedOptions);
      });

    combineLatest([this.activeRootOption$, this.search$, this.searchResultCap$, this.flattenedOptions$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([root, search, searchResultCap, flattenedOptions]) => {
        if (root) {
          this.activeOptions = root.children;
        } else if (search) {
          const searchResults: ISelectOption[] = this.filterOptions(flattenedOptions, search);
          this.activeOptions = searchResultCap > 0 ? searchResults.slice(0, searchResultCap) : searchResults;
        } else {
          this.activeOptions = this.options$.value;
        }
      });

    this.selectedOptions$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((selectedOptions: Set<ISelectOption>) => {
        this.independentSelectedOptions$.next(this.getindependentSelectedOptions(selectedOptions));
        if (!this.emitOnlyIndependentSelections && this.hasChanged) {
          this.update.next(Array.from(selectedOptions));
        }
      });

    this.independentSelectedOptions$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((independentSelectedOptions: Set<ISelectOption>) => {
        this.onlySelectedOption = independentSelectedOptions.size === 1 ? independentSelectedOptions.values().next().value : null;

        this.selectedOptionsTooltip = Array.from(independentSelectedOptions)
          .sort((a: ISelectOption, b: ISelectOption) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
          .map(option => option.label).join(', ');

        if (this.emitOnlyIndependentSelections && this.hasChanged) {
          this.update.next(Array.from(independentSelectedOptions));
        }
      });
  }

  public ngAfterViewInit(): void {
    if (!this.isExperimentalImplementation) {
      this.resizeObserver.observe(this.dropdownTrigger.nativeElement);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    if (!this.isExperimentalImplementation) {
      this.resizeObserver.unobserve(this.dropdownTrigger.nativeElement);
    }
  }

  public handleMobileViewBackClick(): void {
    if (!this.activeRootOption$.getValue()) {
      this.toggleMobileContentDialog();
      this.searchChanged('');
    } else this.ascend();
  }

  public toggleMobileContentDialog(): void {
    this.dialogOpen = !this.dialogOpen;
    if (!this.dialogOpen) {
      this.dialogClose.emit();
    }
  }

  /**
   * When the dropdown is made invisible, clear the active options.
   * @param isOpen Whether the dropdown is visible.
   */
  public onDropDownMenuVisibilityChange(isOpen: boolean): void {
    if (isOpen) {
      this.search$.next(null);
      this.activeRootOption$.next(null);
    } else this.dropDownClose.emit();
    this.isDropDownMenuOpen = isOpen;
    this.cdr.detectChanges();
  }

  /**
   * Select an option.
   * @param option The option to be selected.
   */
  public selectOption(option: ISelectOption): void {
    const isRootOption: boolean = this.isRootOption(option);
    if (this.isRadioSelectionEnabled && isRootOption) {
      this.selectedOptions$.next(new Set());
      this.indeterminateOptions.clear();
    }

    this.hasChanged = true;
    const selectedOptionsState: Set<ISelectOption> = this.selectedOptions$.value;

    // Update the option and its descendants.
    const isPreSelected: boolean = selectedOptionsState.has(option);
    this.changeOptionSelectionState(
      option, selectedOptionsState, this.indeterminateOptions, !isPreSelected,
    );

    // Update the option's ancestors (from parent->root, so no recursion is necessary).
    const optionAncestors: ISelectOption[] = (this.ancestors.get(option) || []).slice().reverse();
    optionAncestors.forEach((ancestor: ISelectOption) =>
      this.calculateOptionStateByChildren(ancestor, selectedOptionsState, this.indeterminateOptions, false),
    );

    this.selectedOptions$.next(selectedOptionsState);

    if (this.isRadioSelectionEnabled && !isRootOption) {
      const rootAncestor: ISelectOption = optionAncestors[optionAncestors.length - 1];
      if (this.areMultipleRootOptionsSelected) {
        this.options$.value.filter((rootOption: ISelectOption): boolean => rootOption.key !== rootAncestor.key)
          .forEach((rootOption: ISelectOption): void => {
            this.changeOptionSelectionState(
              rootOption, selectedOptionsState, this.indeterminateOptions, false,
            );
          });
        this.selectedOptions$.next(selectedOptionsState);
      }
    }
  }

  /**
   * Check if the option is the root option.
   * @param option The option.
   * @returns Return true if the option is a root.
   */
  private isRootOption(option: ISelectOption): boolean {
    return !this.getParent(option);
  }
   /**
    * Check if the any of the options at Top Root Level is Selected or not.
    * @param options The options.
    * @returns Returns true if the option is selected.
    */
  private get areMultipleRootOptionsSelected(): boolean {
    return Array.from(this.selectedOptions$.value).some((rootOption: ISelectOption): boolean => rootOption.isPreSelected);
  }

  /**
   * Empty the selected options.
   */
  public clear(): void {
    this.hasChanged = true;
    this.selectedOptions$.next(new Set());
    if (this.closeDropdownOnClear) this.onDropDownMenuVisibilityChange(false);
    this.indeterminateOptions.clear();

    if (this.isExperimentalImplementation && this.isMobileView) { this.toggleMobileContentDialog(); }
  }

  /**
   * Update the search query.
   * @param search The new search query.
   */
  public searchChanged(search: string): void {
    this.search$.next(search);
    this.cdr.detectChanges();
  }

  /**
   * Move one level higher (toward the root) in the option hierarchy.
   */
  public ascend(): void {
    let parent: ISelectOption = this.activeRootOption$.value;
    do {
      parent = this.getParent(parent);
    } while (parent && parent.isHeader);

    this.activeRootOption$.next(parent);
  }

  /**
   * Move to a particular sub-option.
   * @param option The sub-option to become the new root.
   */
  public descend(option: ISelectOption): void {
    this.activeRootOption$.next(option);
  }

  /**
   * When the window is resized, match the width of the dropdown to the width of the trigger button.
   */
  private onWindowResize() {
    if (this.dropdownTrigger.nativeElement.offsetWidth > DEFAULT_DROPDOWN_MAX_WIDTH) {
      this.dropdownWidthOverride = `${this.dropdownTrigger.nativeElement.offsetWidth}px`;
    } else {
      this.dropdownWidthOverride = undefined;
    }
  }

  /**
   * Get the parent option of an option.
   * @param option The option.
   * @returns The parent of the option (or null, if the option is a root).
   */
  private getParent(option: ISelectOption): ISelectOption | null {
    const ancestors: ISelectOption[] = this.ancestors.get(option);
    return ancestors.length > 0 ? ancestors[ancestors.length - 1] : null;
  }

  /**
   * Recursively convert the hierarchy of options into a flattened array.
   * @param options The hierarchy of options.
   * @returns A flattened list of options.
   */
  private flattenOptions(options: ISelectOption[]): ISelectOption[] {
    return options.concat(flatten(options.map((option: ISelectOption) => this.flattenOptions(option.children || []))));
  }

  /**
   * Recursively convert the hierarchy of options into a mapping of options to their ancestors (in parent->root order).
   * @param options The hierarchy of options.
   * @param ancestors The pre-existing ancestors of the root options.
   * @returns The mapping of options to their ancestors (in parent->root order).
   */
  private mapAncestors(options: ISelectOption[], ancestors: ISelectOption[] = []): Map<ISelectOption, ISelectOption[]> {
    const map: Map<ISelectOption, ISelectOption[]> = new Map();
    options.forEach((option: ISelectOption) => {
      map.set(option, ancestors);
      if (option.children && option.children.length > 0) {
        this.mapAncestors(option.children, ancestors.concat(option))
          .forEach((childAncestors: ISelectOption[], child: ISelectOption) => {
            map.set(child, childAncestors);
          });
      }
    });
    return map;
  }

  /**
   * Collect the options whose labels match a search query. Requires a FLATTENED list of options.
   * @param options A FLATTENED list of options.
   * @param search The search query.
   * @returns The options whose labels match the search query.
   */
  private filterOptions(options: ISelectOption[], search: string): ISelectOption[] {
    if (!search) {
      return options;
    } else {
      return options.filter((opt: ISelectOption) => {
        return !opt.isHeader && opt.label.toLowerCase().indexOf(search.toLowerCase()) > -1;
      });
    }
  }

  /**
   * Respect the isPreSelected attribute of a hierarchy of options.
   * @param options The hierarchy of options.
   * @param flattenedOptions The flattened options.
   */
  private deriveSelectedOptionsFromInitialState(options: ISelectOption[], flattenedOptions: ISelectOption[]): void {
    const selectedOptionsState: Set<ISelectOption> = new Set();
    flattenedOptions.forEach((option: ISelectOption) => {
      if (option.isPreSelected) {
        selectedOptionsState.add(option);
        this.changeOptionSelectionState(option, selectedOptionsState, this.indeterminateOptions, true);
      }
    });
    options.forEach((option: ISelectOption) => {
      this.calculateOptionStateByChildren(option, selectedOptionsState, this.indeterminateOptions, true);
    });
    this.selectedOptions$.next(selectedOptionsState);
  }

  /**
   * Count the number of children of a particular option which are selected or interminate.
   * @param option The option whose children's states are being analyzed.
   * @param selectedOptionsState A set of all selected options.
   * @param indeterminateOptionsState A set of all indeterminate options.
   * @returns The number of children of the option which are selected or indeterminate.
   */
  private countSelectedOrIndeterminateChildren(
    option: ISelectOption,
    selectedOptionsState: Set<ISelectOption>,
    indeterminateOptionsState: Set<ISelectOption>,
  ): { selected: number; indeterminate: number; } {
    if (option.children && option.children.length > 0) {
      return option.children.reduce(
        (count: { selected: number; indeterminate: number; }, child: ISelectOption) =>
          ({
            selected: count.selected + (selectedOptionsState.has(child) ? 1 : 0),
            indeterminate: count.indeterminate + (indeterminateOptionsState.has(child) ? 1 : 0),
          }),
          { selected: 0, indeterminate: 0 },
      );
    } else {
      return { selected: 0, indeterminate: 0 };
    }
  }

  /**
   * Select or deselect a particular option and recursively select or deselect its descendants.
   * @param option The root option whose descendants must be selected or deselected.
   * @param selectedOptionsState Updated by reference. A set of all selected options.
   * @param indeterminateOptionsState Update by reference. A set of all indeterminate options.
   * @param select True if the descendants should be selected; otherwise, false.
   * @returns The updated selected options and indeterminate options.
   */
  private changeOptionSelectionState(
    option: ISelectOption,
    selectedOptionsState: Set<ISelectOption>,
    indeterminateOptionsState: Set<ISelectOption>,
    select: boolean,
  ): {
    selectedOptionsState: Set<ISelectOption>;
    indeterminateOptionsState: Set<ISelectOption>;
  } {
    // Update the option.
    indeterminateOptionsState.delete(option);
    if (select) {
      selectedOptionsState.add(option);
    } else {
      selectedOptionsState.delete(option);
    }

    // Update the option's children.
    if (option.children && option.children.length > 0) {
      option.children.forEach((child: ISelectOption) =>
        this.changeOptionSelectionState(child, selectedOptionsState, indeterminateOptionsState, select),
      );
    }

    return {
      selectedOptionsState,
      indeterminateOptionsState,
    };
  }

  /**
   * Calculate the selection state (selected/unselected/indeterminate) of a particular option.
   * @param option The root option whose state must be determined.
   * @param selectedOptionsState Updated by reference. A set of all selected options.
   * @param indeterminateOptionsState Updated by reference. A set of all indeterminate options.
   * @param recurse Set to true if the children of the root option have not had their states calculated;
   * otherwise, set to false for better efficiency.
   * @returns The updated selected options and indeterminate options.
   */
  private calculateOptionStateByChildren(
    option: ISelectOption,
    selectedOptionsState: Set<ISelectOption>,
    indeterminateOptionsState: Set<ISelectOption>,
    recurse: boolean,
  ): {
    selectedOptionsState: Set<ISelectOption>;
    indeterminateOptionsState: Set<ISelectOption>;
  } {
    if (option.children && option.children.length > 0) {
      if (recurse) {
        /*
        Move to the leaf nodes so that the children of this option will have
        freshly-calculated information.
        */
        option.children.forEach((child: ISelectOption) =>
          this.calculateOptionStateByChildren(child, selectedOptionsState, indeterminateOptionsState, true),
        );
      }

      /*
      Since the children of this option have freshly-calculated information, only the
      states of the direct children need to be looked at to determine the current
      option's selection state.
      */
      const wasSelected: boolean = selectedOptionsState.has(option);
      selectedOptionsState.delete(option);
      indeterminateOptionsState.delete(option);
      const { selected, indeterminate } = this.countSelectedOrIndeterminateChildren(
        option, selectedOptionsState, indeterminateOptionsState,
      );

      if (selected + indeterminate > 0) {
        if (wasSelected && selected === option.children.length && !option.isHeader) {
          selectedOptionsState.add(option);
        } else {
          /*
          We are using the radio buttons at root level if the isRadioSelectionEnabled is enabled,
          radio buttons are either true or false, they don't have any indeterminate state,
          hence if the current option is at root level and is partially enabled (i.e in indeterminate state)
          then we are marking it as selected option.
          */
          if (this.isRadioSelectionEnabled && this.isRootOption(option)) selectedOptionsState.add(option);
          else indeterminateOptionsState.add(option);

        }
      }
    }

    return {
      selectedOptionsState,
      indeterminateOptionsState,
    };
  }

  /**
   * Filter the selected options to get only selected options whose parents are not selected.
   * @param selectedOptions Set of all options which are selected, either directly or indirectly.
   * @returns The selected options whose parents are not selected.
   */
  private getindependentSelectedOptions(selectedOptions: Set<ISelectOption>): Set<ISelectOption> {
    const set: Set<ISelectOption> = new Set();
    selectedOptions.forEach((option: ISelectOption) => {
      const parent: ISelectOption = this.getParent(option);
      if (!parent || !selectedOptions.has(parent)) {
        set.add(option);
      }
    });
    return set;
  }
}
